import {
  Box,
  Button,
  Flex,
  FlexProps,
  GridItem,
  Heading,
  Image,
  SimpleGrid,
  Stack,
  StackProps,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { Carousel } from "../components/Carousel";
import { CenteredExpandedImage } from "../components/CenteredExpandedImage";
import { ColouredTickList } from "../components/ColouredTickList";
import { FirstPageHeader } from "../components/FirstPageHeader";
import { LocationCard } from "../components/LocationCard";
import { MinCaravanPrice } from "../components/MinCaravanPrice";
import { BLUE_COLOR, IMAGE_PATH, MAX_CONTENT_WIDTH, NAV_HEIGHT, SITE_MIN_MARGIN } from "../const";
import { SearchFilterForm } from "../forms/SearchFilterForm";
import { Helmet } from "react-helmet";
import insuranceImage1 from "../images/insurance-main.jpg";

const HOMEPAGE_TICK_LIST_DATA = [
  "Search and Compare a Huge Selection of UK Static Caravans for Sale",
  "Featuring Industry Leading UK Holiday Park Operators",
  "UK Holiday Aggregator Established in 1989",
  "Search, Compare, Book Visit",
];

const POPULAR_LOCATION_DATA = [
  {
    location: "Wales",
    image: `${IMAGE_PATH}/wales.jpg`,
    description:
      "Luxury holiday homes in Wales provide stunning coastal settings with sandy beaches alongside hugely popular tourist attractions such as the world famous Snowdonia National Park.",
  },
  {
    location: "Devon",
    image: `${IMAGE_PATH}/devon.jpg`,
    description:
      "Devon offers a perfect mix of adventure, relaxation, and discovery. Holiday homes in Devon deliver a unique blend of picturesque landscaped from the rugged cliffs of the Devon coast to the tranquil beaches of the South.",
  },
  {
    location: "Cornwall",
    image: `${IMAGE_PATH}/cornwall.jpg`,
    description:
      "Holiday homes in Cornwall boast unforgettable coastal vistas with pristine sandy beaches that allow you to explore the breathtaking landscapes and historical sites that make Cornwall a captivating destination for all the family.",
  },
  {
    location: "Dorset",
    image: `${IMAGE_PATH}/dorset.jpg`,
    description:
      "Owning a holiday home in Dorset delivers the ideal base to explore the surrounding natural beauty and rich history. From the dramatic Jurassic Coast to the gentle rolling hills of the countryside, Dorset provides a picturesque backdrop for adventures all year round.",
  },
];

const OPERATOR_CARDS = [
  { name: "Park Holidays", imageLink: "park-holidays-uk.jpeg" },
  // { name: "Parkdean Resorts", imageLink: "parkdean-resorts.jpeg" },
  { name: "Park Leisure", imageLink: "park-leisure.jpg" },
];

export const Home: React.FC = () => {
  const slidesToShowBase = useBreakpointValue({ base: 1, md: 2, lg: 3 }) || 1;
  const slidesToShow = Math.min(slidesToShowBase, OPERATOR_CARDS.length);

  const showFilterForm = useBreakpointValue({ base: true, md: false });

  return (
    <>
      <Helmet>
        <title>UK Holiday Homes for Sale</title>
        <meta name="description" content="Search a huge selection of Holiday Homes for Sale across the UK" />
      </Helmet>

      <Box w="100%" mb={10}>
        <Box minH="650px" h="650px">
          <Flex
            position="absolute"
            top={NAV_HEIGHT}
            zIndex={-1}
            backgroundImage={`${IMAGE_PATH}/bfhh-new-cover.jpg`}
            backgroundSize="cover"
            backgroundPosition="center"
            minH="650px"
            w="100%"
            p={SITE_MIN_MARGIN}
            flex={1}
          />
          <FirstPageHeader />
        </Box>

        <MinCaravanPrice />

        {showFilterForm && <SearchFilterForm pt={4} pb={8} px={{ base: 4, sm: 8 }} bg={BLUE_COLOR} borderRadius={0} />}

        <MainContentContainer>
          {/* Top Section */}

          <Flex gap={8} flexDir={{ base: "column", lg: "row" }} pb={8}>
            <CenteredExpandedImage src={`${IMAGE_PATH}/bfhh-park-image-new.jpg`} w={{ base: "100%", lg: "50%" }} />

            <TextSection1 w={{ base: "100%", lg: "50%" }} />
          </Flex>

          <TextSection2 />

          <Carousel extraSettings={{ slidesToShow }} className="full-width-carousel">
            {OPERATOR_CARDS.map((opReg) => (
              <Box key={opReg.name}>
                <CenteredExpandedImage src={`${IMAGE_PATH}/${opReg.imageLink}`} h="275px" w="100%" />
                <Box p={2} borderWidth={1}>
                  <Text fontWeight="semibold" color={BLUE_COLOR}>
                    {opReg.name}
                  </Text>
                </Box>
              </Box>
            ))}
          </Carousel>

          {/* Middle Section */}
          <Heading textAlign="center" pt={8} pb={4}>
            Top UK Locations for{" "}
            <Text as="span" fontWeight="bold">
              Holiday Homes
            </Text>
          </Heading>

          <SimpleGrid columns={{ base: 2, lg: 4 }} gap={{ base: 8, lg: 4 }} px={{ base: 2, lg: 0 }}>
            {POPULAR_LOCATION_DATA.map(({ location, image, description }) => (
              <GridItem key={location} as={Flex} grow={1}>
                <LocationCard location={location} image={image} description={description} />
              </GridItem>
            ))}
          </SimpleGrid>

          {/* Bottom Section */}
          <Flex mt={16} flexDir={{ base: "column", lg: "row" }} gap={{ base: 8, lg: 4 }}>
            <TextSection3 w={{ base: "100%", lg: "50%" }} />

            <Box mx="auto">
              <Image src={`${IMAGE_PATH}/bfhh-buying-guide.jpg`} />
            </Box>
          </Flex>

          <Flex mt={16} flexDir={{ base: "column", lg: "row" }} gap={{ base: 8, lg: 4 }}>
            <TextSection4 w={{ base: "100%", lg: "50%" }} />

            <Box mx="auto">
              <Image src={insuranceImage1} />
            </Box>
          </Flex>
        </MainContentContainer>
      </Box>
    </>
  );
};

const MainContentContainer: React.FC<FlexProps> = (props) => (
  <Flex
    p={SITE_MIN_MARGIN}
    alignItems="center"
    flexDir="column"
    mt={10}
    maxW={MAX_CONTENT_WIDTH}
    mx="auto"
    {...props}
  />
);

const TextSection1: React.FC<StackProps> = (props) => (
  <Stack spacing={2} {...props}>
    <Heading>
      Breakfree Holidays -{" "}
      <Text as="span" fontWeight="bold">
        Holiday Homes for Sale
      </Text>
    </Heading>

    <ColouredTickList data={HOMEPAGE_TICK_LIST_DATA} columns={1} gap={1} />
    <Text>
      The Breakfree Holidays team have been helping holidaymakers find their perfect family home across the UK at the
      nation's most popular holiday parks.
    </Text>
    <Text>
      Search our site now and we will match your specific holiday home requirements to holiday parks and holidays homes
      for sale from our featured parks. Once complete you will be emailed your holiday park and home home matches, and
      you will be contacted directly by the park operators. It is that simple!
    </Text>
  </Stack>
);

const TextSection2: React.FC<StackProps> = (props) => {
  // TODOxDW Hide for now, we'll probably reinsert this later
  // const { numberOfParks, numberOfCaravans } = useGetParksAndCaravansQuantity();

  return (
    <Stack align="center" spacing={0} pb={8} {...props}>
      <Heading pb={2}>Search Holiday Homes for Sale</Heading>
      {/* <Text pb={2}>
        Search {numberOfParks} parks & {numberOfCaravans.toLocaleString()} static caravans for sale
      </Text> */}
      <Text align="center">
        We are proud to work with industry-leading parks, that feature fantastic facilities with something for the
        entire family. Our comprehensive holiday home search functionality allows you to search and discover the perfect
        holiday homes for your requirements in a matter of clicks.
      </Text>
    </Stack>
  );
};

const TextSection3: React.FC<StackProps> = (props) => (
  <Stack gap={4} {...props}>
    <Heading lineHeight="1.25">
      The Breakfree Guide To
      <br /> Buying Your{" "}
      <Text as="span" fontWeight="bold">
        Holiday Home
      </Text>
    </Heading>

    <Text>
      Buying a holiday home can be one of the most exciting and rewarding decisions you can make. Owning your very own
      get away by the coast or in the countryside is many people's dream. But it's a big decision and you need to make
      the right one, so at Breakfree Holiday Homes we have put together a Buyers Guide to help you on your journey.
    </Text>
    <Text>
      We have many years' experience dealing with many of the large holiday park groups and independent operators, so we
      are in a good position to offer friendly advice and helpful information. So, check out our guide to the top 10
      things to think about.
    </Text>

    <Link to="blog" style={{ width: "100%" }}>
      <Button width="300px" color="white" variant="blue">
        Read more
      </Button>
    </Link>
  </Stack>
);

const TextSection4: React.FC<StackProps> = (props) => (
  <Stack gap={4} {...props}>
    <Heading lineHeight="1.25">
      Looking for{" "}
      <Text as="span" fontWeight="bold">
        motorhome insurance?
      </Text>
      <br /> Why not check out our trusted partners
    </Heading>

    <Text>
      Looking for motorhome insurance? You’re in the right place. We’ve teamed up with a selection of trusted partners
      to bring you competitive quotes and quality cover – all in one convenient spot. Whether you’re planning a weekend
      getaway or a long road trip, peace of mind is just a few clicks away. Why not take a look and find the policy that
      suits you best?
    </Text>

    <Link to="insurance" style={{ width: "100%" }}>
      <Button width="300px" color="white" variant="blue">
        Read more
      </Button>
    </Link>
  </Stack>
);
